import { useEffect, useState } from "react";
import moment from "moment";

import ReportService from "src/Services/API/report.service";

/**
 * The history of one technician's hours for the range on screen.
 *
 * Every line is a change someone made: who, when, from which screen, to which
 * day, and what it was before and after — as a sentence, built server-side so
 * every reader agrees. Grouped by the day the hours belong to, which is what
 * the breakdown above is grouped by; the time of the change sits on the line,
 * because "Sept 1 was edited on Sept 3" is often the point.
 */

const SOURCE_LABEL = {
  LOG_HOURS_MODAL: "Log Hours · Tech App",
  WO_TIMESHEET_APP: "WO Timesheet · Tech App",
  TIME_CARDS: "Time Cards · Tech App",
  WO_TIMESHEET_BROWSER: "WO Timesheet · Browser App",
  PAYROLL: "Time Management · Browser App",
  UNKNOWN: "",
};

// Section label and right-hand time, the same way the technician activity
// drawer does them, so the two drawers read as one family.
const dayHeading = (date) => {
  const d = moment(date);
  if (d.isSame(moment(), "day")) return "TODAY";
  if (d.isSame(moment().subtract(1, "day"), "day")) return "YESTERDAY";
  return d.format("MMM D, YYYY").toUpperCase();
};

const timeLabel = (date) => {
  const d = moment(date);
  if (d.isSame(moment(), "day")) return d.fromNow();
  if (d.isSame(moment().subtract(1, "day"), "day")) return `Yesterday, ${d.format("h:mm A")}`;
  return d.format("MMM D, h:mm A");
};

const minutesOf = (value) => {
  if (typeof value !== "string" || !/^\d{1,2}:\d{2}$/.test(value)) return 0;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

const hoursText = (minutes) => {
  const hours = Math.round((minutes / 60) * 100) / 100;
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
};

/**
 * The headline: to the point, in the activity drawer's voice. Hours put on or
 * taken off the day say how many — "Logged 4.5 hours" — and everything else
 * is a change, said as the ledger already says it.
 */
const headlineFor = (event) => {
  const entryMinutes = (entry) => minutesOf(entry?.regularTime) + minutesOf(entry?.travelTime);
  if (event.kind === "ENTRY_ADDED") return `Logged ${hoursText(entryMinutes(event.after))}`;
  if (event.kind === "ENTRY_REMOVED") return `Removed ${hoursText(entryMinutes(event.before))}`;
  return event.description;
};

const toDay = (value) => (value ? moment(value).format("YYYY-MM-DD") : null);

// Matches the CSS duration-300 below, so the drawer is unmounted as the slide
// finishes rather than a beat before or after.
const SLIDE_MS = 300;

const HoursHistoryDrawer = ({ technician, range, preloaded, onClose }) => {
  // The technician being shown, held after the parent clears it so the drawer
  // has something to display while it slides out. Clearing on the way out
  // used to unmount it in the same frame, which is why it snapped shut.
  const [shown, setShown] = useState(null);
  const [visible, setVisible] = useState(false);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | error

  useEffect(() => {
    if (technician) {
      setShown(technician);
      return undefined;
    }
    // Slide out first; drop the content only once it is off-screen.
    setVisible(false);
    const timer = setTimeout(() => {
      setShown(null);
      setEvents([]);
      setStatus("idle");
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [technician]);

  // The reveal is a separate effect keyed on the mount, not on the prop.
  // Effects run after paint, so by the time this runs the panel has been
  // painted off-screen; the next frame's class change then has a real
  // starting position to slide from. Scheduling the reveal in the same effect
  // that mounted the panel let both land in one paint — no slide in.
  useEffect(() => {
    if (!shown || !technician) return undefined;
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [shown, technician]);

  useEffect(() => {
    if (!technician?.technicianId || !range?.[0] || !range?.[1]) return;
    let cancelled = false;
    // Prefetched by the page when the hours loaded: show it at once and only
    // refresh in the background, so the drawer never opens onto "Loading…"
    // for a list it already has. Without a prefetch, load as before.
    const havePreloaded = Array.isArray(preloaded);
    if (havePreloaded) {
      setEvents(preloaded);
      setStatus("idle");
    } else {
      setStatus("loading");
      setEvents([]);
    }
    ReportService.getHoursEvents(technician.technicianId, toDay(range[0]), toDay(range[1]))
      .then((response) => {
        if (cancelled) return;
        setEvents(Array.isArray(response.data) ? response.data : []);
        setStatus("idle");
      })
      .catch(() => {
        // A failed refresh of a list already on screen is not worth an error
        // in front of it; only a list we never got is.
        if (!cancelled && !havePreloaded) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technician?.technicianId, range]);

  if (!shown) return null;

  // Grouped by when the change was made — TODAY, YESTERDAY, a date — as the
  // activity drawer is. The day the hours belong to sits on the row when it is
  // a different day, since "Sept 1 edited on Sept 3" is often the point.
  const sorted = [...events].sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt));
  const groups = [];
  for (const event of sorted) {
    const heading = dayHeading(event.createdAt);
    let group = groups.find((entry) => entry.heading === heading);
    if (!group) {
      group = { heading, items: [] };
      groups.push(group);
    }
    group.items.push(event);
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[474px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header, as the technician activity drawer lays it out. */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="min-w-0">
            <div className="font-OpenSansRegular text-[18px] font-[600] capitalize leading-[28px] text-[#1A2332]">
              {shown.technician}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 font-OpenSansRegular text-[13px] leading-[18px]">
              <span className="font-[700] uppercase tracking-[0.13px] text-[#0065FD]">Hours history</span>
              <span className="text-[#D0D5DD]">•</span>
              <span className="font-OpenSansRegular text-[14px] font-[400] leading-[20px] tracking-[0.14px] text-[#868DA6]">
                {range?.[0] && range?.[1]
                  ? `${moment(range[0]).format("MMM D")} – ${moment(range[1]).format("MMM D, YYYY")}`
                  : "Loaded range"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="-mr-1 flex h-8 w-8 items-center justify-center rounded-full text-[#98A2B3] hover:bg-[#F2F4F7]"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="h-px bg-[#EAECF0]" />

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {status === "loading" ? (
            <div className="py-10 text-center font-OpenSansRegular text-[13px] text-[#98A2B3]">Loading…</div>
          ) : status === "error" ? (
            <div className="py-10 text-center font-OpenSansRegular text-[13px] text-[#B91C1C]">
              The history could not be loaded. Try again in a moment.
            </div>
          ) : groups.length === 0 ? (
            <div className="py-10 text-center">
              <div className="font-OpenSansRegular text-[13px] text-[#98A2B3]">No changes in this period</div>
              {/* Said plainly, because a blank history reads as "nothing ever
                  happened" when it may only mean "before we kept a record". */}
              <div className="mt-1 font-OpenSansRegular text-[12px] text-[#B9C0CF]">
                History begins from when the ledger was introduced.
              </div>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.heading} className="mb-2">
                <div className="flex items-center gap-3 py-3">
                  <span className="font-OpenSansRegular text-[12px] font-[600] uppercase tracking-[0.6px] text-[#98A2B3]">
                    {group.heading}
                  </span>
                  <span className="h-px flex-1 bg-[#EAECF0]" />
                </div>
                <div className="flex flex-col">
                  {group.items.map((event, index) => (
                    <HistoryRow key={event.id} event={event} isLast={index === group.items.length - 1} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

// A single timeline row, in the activity drawer's shape: ring dot and
// connector, a bold headline, the work order in blue, a grey context line,
// and the time on the right.
const HistoryRow = ({ event, isLast }) => {
  const affectsOtherDay =
    event.date && !moment(event.date).isSame(moment(event.createdAt), "day");
  const context = [
    event.actorName || "Unknown user",
    SOURCE_LABEL[event.source] || null,
    affectsOtherDay ? `for ${moment(event.date).format("MMM D")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  // The reason only when it is not already in the sentence — the no-lunch
  // reason is.
  const reason =
    event.reason && !(event.description || "").includes(event.reason) ? event.reason : null;

  return (
    <div className="relative flex gap-3">
      <div className="relative flex flex-col items-center">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
            stroke="#0065FD"
          />
          <path
            d="M8 10.5C8.66304 10.5 9.29893 10.2366 9.76777 9.76777C10.2366 9.29893 10.5 8.66304 10.5 8C10.5 7.33696 10.2366 6.70107 9.76777 6.23223C9.29893 5.76339 8.66304 5.5 8 5.5C7.33696 5.5 6.70107 5.76339 6.23223 6.23223C5.76339 6.70107 5.5 7.33696 5.5 8C5.5 8.66304 5.76339 9.29893 6.23223 9.76777C6.70107 10.2366 7.33696 10.5 8 10.5ZM8 7.0625C8.24864 7.0625 8.4871 7.16127 8.66291 7.33709C8.83873 7.5129 8.9375 7.75136 8.9375 8C8.9375 8.24864 8.83873 8.4871 8.66291 8.66291C8.4871 8.83873 8.24864 8.9375 8 8.9375C7.75136 8.9375 7.5129 8.83873 7.33709 8.66291C7.16127 8.4871 7.0625 8.24864 7.0625 8C7.0625 7.75136 7.16127 7.5129 7.33709 7.33709C7.5129 7.16127 7.75136 7.0625 8 7.0625Z"
            fill="#0065FD"
          />
        </svg>
        {!isLast && <span className="-mt-[-3px] -mb-[-3px] w-px flex-1 bg-[#DAEBFF]" />}
      </div>

      <div className="flex flex-1 items-start justify-between gap-3 pb-5">
        <div className="min-w-0">
          <div className="font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] text-[#1A2332]">
            {headlineFor(event)}
          </div>
          {event.woNumber && (
            <a
              href={event.woId ? `/work/${event.woId}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="block font-OpenSansSemiBold text-[12px] font-[600] leading-[18px] tracking-[0.12px] text-[#0065FD] hover:underline"
            >
              WO-{event.woNumber}
            </a>
          )}
          <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] line-clamp-2">
            {context}
          </div>
          {reason && (
            <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477] italic">
              “{reason}”
            </div>
          )}
        </div>
        <span className="whitespace-nowrap pt-0.5 font-OpenSansRegular text-[12px] font-normal leading-[18px] tracking-[0.12px] text-[#868DA6]">
          {timeLabel(event.createdAt)}
        </span>
      </div>
    </div>
  );
};

export default HoursHistoryDrawer;
